import { Box, Button, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addMessage, bonoBotState, setSelectedCharities } from 'store/bonoBot/bonoBot.slice';
import { CharityExampleType } from 'store/bonoBot/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useSocketInstanceContext } from 'context/socketInstanceContext';
import DynamicGoogleIcon from 'components/dynamic-icon/dynamicGoogleIcon';
import CharityModal from 'components/charityModal/charityModal';
import { CharityType } from 'store/causes/types';

const CharityExample: React.FC<CharityExampleType> = (charity: CharityExampleType) => {
   const { t } = useTranslation();
   const { title, image, icon, shortDescription, causes, id, name, isLastMessage } = charity;
   const dispatch = useAppDispatch();
   const { sendMessageToServer } = useSocketInstanceContext();
   const { charitiesList, selectedCharities, storedThreadId } = useAppSelector(bonoBotState);
   const [charityModalData, setCharityModalData] = useState<CharityType | null>(null);

   const getUpdatedCharityExample = () => {
      if (name && id) {
         const charityToShow = charitiesList.find((ch) => ch.id === id);
         if (charityToShow) {
            return charityToShow;
         }
      }
      return charity;
   };

   const charityToShow = getUpdatedCharityExample();

   if (charitiesList.length === 0 || !charitiesList.some((ch) => ch.id === id)) {
      return null;
   }

   const checkIfCanSuggestAnotherCharity = (chList: CharityType[], selCharities: CharityType[]) => {
      if (selCharities.length === 2) return false;
      const selectedIds = new Set(selCharities.map((c) => c.id));
      return chList.some((ch) => !selectedIds.has(ch.id));
   };

   const canShowAnotherCharity = checkIfCanSuggestAnotherCharity(charitiesList, selectedCharities);

   const handleChooseCharity = () => {
      dispatch(setSelectedCharities([...selectedCharities, charityToShow]));
      dispatch(addMessage({ role: 'user', text: `Yes, I want to choose ${title ?? 'this'} charity` }));
      sendMessageToServer('Yes', storedThreadId);
   };

   const handleShowAnother = () => {
      const text = `Show me another charity.`;
      dispatch(addMessage({ role: 'user', text }));
      sendMessageToServer(`don't select this charity, ${text}`, storedThreadId);
   };
   const handleChooseAmount = () => {
      const text = `Let's choose a donation amount.`;
      dispatch(addMessage({ role: 'user', text }));
      sendMessageToServer(text, storedThreadId);
   };

   return (
      <>
         <Box
            sx={{
               width: '100%',
               backgroundColor: '#FFFFFF',
               borderRadius: '8px',
               overflow: 'hidden',
               border: '1px solid #00000010',
            }}
         >
            <Box sx={{ flex: 1, cursor: 'pointer' }}>
               <Box sx={{ width: '100%', height: '100%', maxHeight: '140px', overflow: 'hidden' }}>
                  <Box
                     sx={{ width: '100%' }}
                     component='img'
                     src={image ?? charityToShow?.image}
                     alt={title ?? charityToShow?.title}
                  />
               </Box>
               <Box
                  sx={{
                     mt: '-70px',
                     p: 1.25,
                     height: '100%',
                     width: '100%',
                     display: 'flex',
                     flexDirection: 'column',
                     justifyContent: 'space-between',
                     rowGap: '10px',
                  }}
               >
                  <Box
                     sx={{ width: '70px', height: '70px', borderRadius: '4px' }}
                     component='img'
                     src={icon ?? charityToShow?.icon}
                     alt={title ?? charityToShow?.title}
                  />
                  <Box>
                     <Typography
                        sx={{
                           color: '#333333',
                           fontSize: '14px',
                           fontWeight: 600,
                           lineHeight: '17px',
                        }}
                     >
                        {title ?? charityToShow?.title}
                     </Typography>
                     <Typography
                        sx={{
                           color: '#999999',
                           fontSize: '12px',
                           fontWeight: 600,
                           lineHeight: '14px',
                        }}
                     >
                        {shortDescription ?? charityToShow?.shortDescription}
                     </Typography>
                     <Box sx={{ display: 'flex', flexDirection: 'row', gap: '4px', mt: 0.5, mb: 0.25 }}>
                        <Box
                           sx={{
                              p: 0.5,
                              borderRadius: '4px',
                              backgroundColor: '#FBEAEA',
                              width: 'fit-content',
                              border: '1px solid #F8DCDB',
                           }}
                        >
                           <Typography
                              sx={{
                                 color: '#333333',
                                 fontSize: '10px',
                                 fontWeight: 700,
                                 lineHeight: '10px',
                              }}
                           >
                              {causes?.[0]?.impactHashtag ?? charityToShow?.causes?.[0]?.impactHashtag}
                           </Typography>
                        </Box>
                     </Box>
                  </Box>
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                     <Button
                        onClick={() => setCharityModalData(title ? charity : charityToShow)}
                        sx={{
                           display: 'flex',
                           flexDirection: 'row',
                           gap: '4px',
                           alignItems: 'center',
                           borderRadius: '8px',
                           border: '1px solid #00000010',
                        }}
                     >
                        <DynamicGoogleIcon iconColor='#666666' iconSizeVariant='small' iconName='info' />
                        <Typography
                           sx={{
                              fontSize: '14px',
                              fontWeight: 600,
                              lineHeight: '17px',
                              color: '#666666',
                              textTransform: 'none',
                           }}
                        >
                           {t('bonoBot.charityExample.cta-moreInfo')}
                        </Typography>
                     </Button>
                  </Box>
               </Box>
            </Box>
         </Box>

         <Box
            sx={{
               display: 'flex',
               flexDirection: 'row',
               flexWrap: 'wrap',
               gap: '4px',
               mt: isLastMessage && selectedCharities.length < 3 ? 1.25 : 0,
            }}
         >
            {isLastMessage && selectedCharities.length < 3 && (
               <>
                  <Box
                     onClick={handleChooseCharity}
                     sx={{
                        cursor: 'pointer',
                        border: '1px solid #00000010',
                        borderRadius: '8px',
                        backgroundColor: '#FFFFFF',
                        px: 1.5,
                        py: 1.375,
                     }}
                  >
                     <Typography sx={{ color: '#666666', fontSize: '14px', lineHeight: '17px', fontWeight: 600 }}>
                        {t('bonoBot.charityExample.cta-chooseThisCharity')}
                     </Typography>
                  </Box>
                  {canShowAnotherCharity && (
                     <Box
                        onClick={handleShowAnother}
                        sx={{
                           cursor: 'pointer',
                           border: '1px solid #00000010',
                           borderRadius: '8px',
                           backgroundColor: '#FFFFFF',
                           px: 1.5,
                           py: 1.375,
                        }}
                     >
                        <Typography sx={{ color: '#666666', fontSize: '14px', lineHeight: '17px', fontWeight: 600 }}>
                           {t('bonoBot.charityExample.cta-showAnother')}
                        </Typography>
                     </Box>
                  )}
                  {selectedCharities.length > 0 && (
                     <Box
                        onClick={handleChooseAmount}
                        sx={{
                           cursor: 'pointer',
                           border: '1px solid #00000010',
                           borderRadius: '8px',
                           backgroundColor: '#FFFFFF',
                           px: 1.5,
                           py: 1.375,
                        }}
                     >
                        <Typography sx={{ color: '#666666', fontSize: '14px', lineHeight: '17px', fontWeight: 600 }}>
                           {t('bonoBot.charityExample.cta-chooseAmount')}
                        </Typography>
                     </Box>
                  )}
               </>
            )}
         </Box>

         <CharityModal charityModalData={charityModalData} handleClose={() => setCharityModalData(null)} />
      </>
   );
};

export default CharityExample;
